import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { withRouter, useHistory } from 'react-router';
import { Card, Image, Carousel, Col, Modal, Row, message } from 'antd';
import { ArrowRightOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useWindowWidth } from '@react-hook/window-size';
import { emptyImage } from '../../extensions/helper';
import { serverFetch } from '../../server';
import './home.css';
import DOMPurify from 'dompurify';

const sanitizeDescription = (html) =>
    DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        FORBID_TAGS: ['a', 'object', 'iframe'],
    });

const HomeCarouselPrevArrow = ({ className, style, onClick }) => (
    <button type='button' className={`home-carousel-arrow home-carousel-arrow--prev ${className || ''}`} style={style} onClick={onClick} aria-label='Назад'>
        <LeftOutlined />
    </button>
);

const HomeCarouselNextArrow = ({ className, style, onClick }) => (
    <button type='button' className={`home-carousel-arrow home-carousel-arrow--next ${className || ''}`} style={style} onClick={onClick} aria-label='Вперёд'>
        <RightOutlined />
    </button>
);

const Home = () => {
    const emptyGroups = [{ id: 0, name: '', description: '' }];
    const history = useHistory();
    const user = useSelector((s) => s.user);
    const company = useSelector((s) => s.company);
    const onlyWidth = useWindowWidth();
    const [groups, setGroups] = useState(emptyGroups);
    const [loading, setLoading] = useState(false);
    const [descriptionOpen, setDescriptionOpen] = useState(false);
    const [descriptionHeight, setDescriptionHeight] = useState(null);
    const bannerRef = useRef(null);

    useEffect(() => {
        if (company && company.id > 0) {
            setLoading(true);
            let queryParams = {
                companyId: company.id,
                isActive: true,
            };
            serverFetch('catalog/groups', { method: 'GET', queryParams: queryParams }, user)
                .then((data) => {
                    setGroups(data);
                    setLoading(false);
                })
                .catch(() => {
                    message.error('Ошибка получения групп каталога');
                    setLoading(false);
                });
        }
    }, [company, user]);

    const openGroup = (groupId) => {
        history.push(`/catalog/${groupId}`);
    };

    const qtyCards = Math.min(onlyWidth >= 1500 ? 3 : onlyWidth >= 744 ? 2 : 1, groups.length);
    const bannerImage = company ? company.loadingPerDayImage || '/images/banner.jpg' : null;
    const hasBanner = Boolean(bannerImage);
    const hasDescription = Boolean(company?.description);
    const descriptionHtml = hasDescription ? sanitizeDescription(company.description) : '';
    const isDesktopHero = onlyWidth >= 992;
    const descriptionClipped = hasDescription && hasBanner && isDesktopHero;

    useEffect(() => {
        if (!descriptionClipped) {
            setDescriptionHeight(null);
            setDescriptionOpen(false);
            return undefined;
        }

        const bannerEl = bannerRef.current;
        if (!bannerEl) {
            return undefined;
        }

        const syncHeight = () => {
            setDescriptionHeight(bannerEl.getBoundingClientRect().height);
        };

        syncHeight();

        const observer = new ResizeObserver(syncHeight);
        observer.observe(bannerEl);
        window.addEventListener('resize', syncHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', syncHeight);
        };
    }, [descriptionClipped, bannerImage]);

    const renderDescriptionHtml = (className) => (
        <div className={className} dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
    );

    const renderGroups = () => {
        return (
            groups && (
                <div className='group-container'>
                    <Carousel
                        arrows
                        infinite
                        slidesToShow={qtyCards}
                        slidesToScroll={1}
                        dots
                        prevArrow={<HomeCarouselPrevArrow />}
                        nextArrow={<HomeCarouselNextArrow />}
                    >
                        {groups.map((g) => {
                            const description = g.description?.trim() ? g.description : 'Описание отсутствует';
                            return (
                                <Card
                                    className='home-group-card'
                                    key={g.id}
                                    cover={<img alt={g.name} src={g.image || emptyImage} className='home-group-card-cover' />}
                                    onClick={() => openGroup(g.id)}
                                    hoverable={true}
                                    loading={loading}
                                    bordered={false}
                                >
                                    <div className='home-group-card-body'>
                                        <div className='home-group-title'>{g.name}</div>
                                        <div className='home-group-description'>{description}</div>
                                        <div className='home-group-link'>
                                            Подробнее о тарифах <ArrowRightOutlined />
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}
                    </Carousel>
                </div>
            )
        );
    };

    return (
        <div className='block-content home-page'>
            {(hasDescription || hasBanner) && (
                <Row gutter={[16, 16]} className='home-hero' align='stretch'>
                    {hasDescription && (
                        <Col
                            xs={{ span: 24, order: 2 }}
                            lg={{ span: hasBanner ? 12 : 24, order: 1 }}
                            className='home-hero-col'
                        >
                            <div
                                className={`home-hero-panel home-hero-description${descriptionClipped ? ' home-hero-description--clipped' : ''}`}
                                style={
                                    descriptionHeight
                                        ? { height: descriptionHeight, minHeight: descriptionHeight }
                                        : undefined
                                }
                                onClick={descriptionClipped ? () => setDescriptionOpen(true) : undefined}
                                onKeyDown={
                                    descriptionClipped
                                        ? (e) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  setDescriptionOpen(true);
                                              }
                                          }
                                        : undefined
                                }
                                role={descriptionClipped ? 'button' : undefined}
                                tabIndex={descriptionClipped ? 0 : undefined}
                            >
                                {renderDescriptionHtml('home-hero-description-content')}
                            </div>
                        </Col>
                    )}
                    {hasBanner && (
                        <Col
                            xs={{ span: 24, order: 1 }}
                            lg={{ span: hasDescription ? 12 : 24, order: 2 }}
                            className='home-hero-col'
                        >
                            <a
                                ref={bannerRef}
                                className='home-hero-panel home-hero-banner'
                                href={company?.adUrl ?? '#'}
                                target='_blank'
                                rel='noreferrer'
                            >
                                <Image src={bannerImage} preview={false} />
                            </a>
                        </Col>
                    )}
                </Row>
            )}
            <Row>
                {company?.showCatalogue && (
                    <Col span={24} className='home-groups'>
                        {renderGroups()}
                    </Col>
                )}
            </Row>
            {isDesktopHero && (
                <Modal
                    open={descriptionOpen}
                    onCancel={() => setDescriptionOpen(false)}
                    footer={null}
                    centered
                    width={640}
                    className='home-description-modal'
                    destroyOnClose
                >
                    {renderDescriptionHtml('home-hero-description-modal-body')}
                </Modal>
            )}
        </div>
    );
};

export default withRouter(Home);
